// Battle Music Conductor - Inspired by Dragon Ball and Chrono Trigger
// Epic, melodic combat themes with adaptive intensity
// Using lookahead scheduler for glitch-free playback

import { Howler } from "howler";

export type MusicPhase = "menu" | "playing" | "paused" | "gameOver" | "waveComplete" | "story" | "storyIntro" | "storyPlaying" | "storyVictory" | "genAIVictory";
export type MusicTerrain = "industrial" | "desert" | "arctic" | "forest" | "volcanic" | "cyber";

export interface MusicSnapshot {
  phase: MusicPhase;
  wave: number;
  terrain: MusicTerrain;
  isMultiplayer: boolean;
  aliveEnemies: number;
  totalEnemies: number;
  playerHealthPct: number;
  killsThisWave: number;
  hasBoss: boolean;
}

// Musical notes in Hz
const NOTE = {
  C3: 130.81, D3: 146.83, E3: 164.81, F3: 174.61, G3: 196.00, A3: 220.00, B3: 246.94,
  C4: 261.63, D4: 293.66, E4: 329.63, F4: 349.23, G4: 392.00, A4: 440.00, B4: 493.88,
  C5: 523.25, D5: 587.33, E5: 659.25, F5: 698.46, G5: 783.99, A5: 880.00, B5: 987.77,
};

// Lookahead scheduler constants (anti-glitch)
const LOOKAHEAD_MS = 25.0;        // Frequency of scheduler tick (ms)
const SCHEDULE_AHEAD_TIME = 0.1;  // How far ahead to schedule (seconds)

function clamp01(value: number) {
  return Math.max(0, Math.min(1, value));
}

export class AdaptiveMusicConductor {
  private audioContext: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private compressor: DynamicsCompressorNode | null = null;
  private oscillators: OscillatorNode[] = [];
  private gains: GainNode[] = [];

  private currentTerrain: MusicTerrain = "industrial";
  private currentTension = 0.25;
  private masterVolume = 0.7;
  private musicVolume = 0.5;
  private isMuted = false;
  private playing = false;

  private intervalId: ReturnType<typeof setTimeout> | null = null;
  private currentStep = 0;
  private nextNoteTime = 0.0; // Lookahead: next note time in audioContext time
  private bpm = 140; // Fast tempo for battle

  constructor(initialTerrain: MusicTerrain = "industrial") {
    this.currentTerrain = initialTerrain;
  }

  private getAudioContext(): AudioContext | null {
    if (typeof window === "undefined") return null;

    if (!this.audioContext || this.audioContext.state === "closed") {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    // Resume automaticamente se suspenso - não bloqueia
    if (this.audioContext.state === "suspended") {
      this.audioContext.resume().catch(() => {});
    }

    return this.audioContext;
  }

  /**
   * Força resume do AudioContext (chamar após interação do usuário)
   */
  forceResume() {
    if (this.audioContext && this.audioContext.state === "suspended") {
      this.audioContext.resume().catch(() => {});
    }
  }

  setVolumes(masterVolume: number, musicVolume: number) {
    this.masterVolume = clamp01(masterVolume);
    this.musicVolume = clamp01(musicVolume);
    this.updateMasterVolume();
  }

  setMuted(muted: boolean) {
    this.isMuted = muted;
    this.updateMasterVolume();
  }

  private updateMasterVolume() {
    if (this.masterGain && this.audioContext) {
      const vol = this.isMuted ? 0 : this.masterVolume * this.musicVolume;
      this.masterGain.gain.setValueAtTime(vol, this.audioContext.currentTime);
    }
  }

  setTerrain(terrain: MusicTerrain) {
    this.currentTerrain = terrain;
  }

  start() {
    if (typeof window === "undefined") return;
    if (this.playing) return;

    const ctx = this.getAudioContext();
    if (!ctx) return;

    this.playing = true;
    this.currentStep = 0;

    // Registrar listener para desbloquear audio após interação do usuário
    const unlockAudio = () => {
      if (ctx.state === "suspended") {
        ctx.resume().catch(() => {});
      }
      // Remove listeners após primeiro unlock
      window.removeEventListener("click", unlockAudio);
      window.removeEventListener("touchstart", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
    
    window.addEventListener("click", unlockAudio);
    window.addEventListener("touchstart", unlockAudio);
    window.addEventListener("keydown", unlockAudio);

    // Compressor for punchy battle sound
    this.compressor = ctx.createDynamicsCompressor();
    this.compressor.threshold.value = -18;
    this.compressor.knee.value = 12;
    this.compressor.ratio.value = 6;
    this.compressor.attack.value = 0.002;
    this.compressor.release.value = 0.1;
    this.compressor.connect(ctx.destination);

    // Master gain
    this.masterGain = ctx.createGain();
    this.masterGain.gain.value = this.isMuted ? 0 : this.masterVolume * this.musicVolume;
    this.masterGain.connect(this.compressor);

    // Create oscillators for battle theme
    // 0: Bass (driving rhythm)
    const bassOsc = ctx.createOscillator();
    const bassGain = ctx.createGain();
    const bassFilter = ctx.createBiquadFilter();
    bassOsc.type = "sawtooth";
    bassFilter.type = "lowpass";
    bassFilter.frequency.value = 200;
    bassGain.gain.value = 0;
    bassOsc.connect(bassFilter);
    bassFilter.connect(bassGain);
    bassGain.connect(this.masterGain);
    bassOsc.start();
    this.oscillators.push(bassOsc);
    this.gains.push(bassGain);

    // 1: Melody (heroic lead)
    const melodyOsc = ctx.createOscillator();
    const melodyGain = ctx.createGain();
    const melodyFilter = ctx.createBiquadFilter();
    melodyOsc.type = "triangle";
    melodyFilter.type = "lowpass";
    melodyFilter.frequency.value = 2500;
    melodyGain.gain.value = 0;
    melodyOsc.connect(melodyFilter);
    melodyFilter.connect(melodyGain);
    melodyGain.connect(this.masterGain);
    melodyOsc.start();
    this.oscillators.push(melodyOsc);
    this.gains.push(melodyGain);

    // 2: Power chord root
    const chordOsc1 = ctx.createOscillator();
    const chordGain1 = ctx.createGain();
    chordOsc1.type = "sawtooth";
    chordGain1.gain.value = 0;
    chordOsc1.connect(chordGain1);
    chordGain1.connect(this.masterGain);
    chordOsc1.start();
    this.oscillators.push(chordOsc1);
    this.gains.push(chordGain1);

    // 3: Power chord fifth
    const chordOsc2 = ctx.createOscillator();
    const chordGain2 = ctx.createGain();
    chordOsc2.type = "sawtooth";
    chordGain2.gain.value = 0;
    chordOsc2.connect(chordGain2);
    chordGain2.connect(this.masterGain);
    chordOsc2.start();
    this.oscillators.push(chordOsc2);
    this.gains.push(chordGain2);

    // 4: High tension layer
    const tensionOsc = ctx.createOscillator();
    const tensionGain = ctx.createGain();
    tensionOsc.type = "sine";
    tensionGain.gain.value = 0;
    tensionOsc.connect(tensionGain);
    tensionGain.connect(this.masterGain);
    tensionOsc.start();
    this.oscillators.push(tensionOsc);
    this.gains.push(tensionGain);

    // 5: Arpeggio
    const arpOsc = ctx.createOscillator();
    const arpGain = ctx.createGain();
    arpOsc.type = "triangle";
    arpGain.gain.value = 0;
    arpOsc.connect(arpGain);
    arpGain.connect(this.masterGain);
    arpOsc.start();
    this.oscillators.push(arpOsc);
    this.gains.push(arpGain);

    // 6: Kick drum
    const kickOsc = ctx.createOscillator();
    const kickGain = ctx.createGain();
    kickOsc.type = "sine";
    kickOsc.frequency.value = 60;
    kickGain.gain.value = 0;
    kickOsc.connect(kickGain);
    kickGain.connect(this.masterGain);
    kickOsc.start();
    this.oscillators.push(kickOsc);
    this.gains.push(kickGain);

    // Initialize lookahead scheduler
    this.nextNoteTime = ctx.currentTime;
    this.scheduler();

    console.log("[MusicConductor] Started - Battle Theme (lookahead scheduler)");
  }

  /**
   * Lookahead scheduler - runs every LOOKAHEAD_MS and schedules notes
   * SCHEDULE_AHEAD_TIME seconds into the future. This prevents glitches
   * even when JS thread is busy with GC or heavy renders.
   */
  private scheduler() {
    if (!this.playing || !this.audioContext) return;

    const ctx = this.audioContext;
    const stepDuration = (60 / this.bpm) / 4;

    // Schedule all notes that fall within the lookahead window
    while (this.nextNoteTime < ctx.currentTime + SCHEDULE_AHEAD_TIME) {
      this.scheduleNote(this.nextNoteTime, stepDuration);
      this.nextNoteTime += stepDuration;
      this.currentStep++;
    }

    // Schedule next scheduler tick
    this.intervalId = setTimeout(() => this.scheduler(), LOOKAHEAD_MS);
  }

  /**
   * Schedules a single step/note at the specified time
   */
  private scheduleNote(time: number, stepDuration: number) {
    if (!this.audioContext || !this.masterGain) return;

    const ctx = this.audioContext;
    const step = this.currentStep;
    const tension = this.currentTension;

    // Chord progression: Am - F - C - G (epic JRPG progression)
    const bar = Math.floor(step / 16) % 4;
    const chordProgression = [
      { root: NOTE.A3, fifth: NOTE.E4, bass: NOTE.A3 * 0.5 },
      { root: NOTE.F3, fifth: NOTE.C4, bass: NOTE.F3 * 0.5 },
      { root: NOTE.C4, fifth: NOTE.G4, bass: NOTE.C3 },
      { root: NOTE.G3, fifth: NOTE.D4, bass: NOTE.G3 * 0.5 },
    ];
    const chord = chordProgression[bar];

    // Heroic battle melody (Dragon Ball / Chrono Trigger inspired)
    const battleMelody = [
      // Phrase 1: Heroic call
      NOTE.A4, 0, NOTE.C5, 0, NOTE.E5, 0, NOTE.A5, NOTE.G5,
      NOTE.E5, 0, NOTE.C5, 0, NOTE.A4, 0, 0, 0,
      // Phrase 2: Rising intensity
      NOTE.F4, 0, NOTE.A4, 0, NOTE.C5, 0, NOTE.F5, NOTE.E5,
      NOTE.C5, 0, NOTE.A4, 0, NOTE.F4, 0, 0, 0,
      // Phrase 3: Triumphant
      NOTE.C5, 0, NOTE.E5, 0, NOTE.G5, 0, NOTE.C5 * 2, NOTE.B5,
      NOTE.G5, 0, NOTE.E5, 0, NOTE.C5, 0, 0, 0,
      // Phrase 4: Resolution
      NOTE.G4, 0, NOTE.B4, 0, NOTE.D5, 0, NOTE.G5, NOTE.F5,
      NOTE.D5, 0, NOTE.B4, 0, NOTE.G4, 0, 0, 0,
    ];

    // Driving bass pattern
    const bassPattern = [1, 0, 0.7, 0, 1, 0, 0.7, 0, 1, 0, 0.7, 0, 1, 0.5, 0.7, 0.5];

    // Power chord hits
    const chordPattern = [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0.8, 0, 1, 0, 0.6, 0];

    // Arpeggio pattern for intensity
    const arpPattern = [1, 0, 0.8, 0, 1, 0, 0.8, 0, 1, 0, 0.8, 0, 1, 0, 0.9, 0];

    // Kick pattern (driving beat)
    const kickPattern = [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 1, 0];

    const [bassOsc, melodyOsc, chordOsc1, chordOsc2, tensionOsc, arpOsc, kickOsc] = this.oscillators;
    const [bassGain, melodyGain, chordGain1, chordGain2, tensionGain, arpGain, kickGain] = this.gains;

    // Bass line
    const bassValue = bassPattern[step % bassPattern.length];
    if (bassValue > 0 && bassOsc && bassGain) {
      bassOsc.frequency.setValueAtTime(chord.bass, time);
      const bassVol = 0.15 * bassValue * (0.6 + tension * 0.4);
      bassGain.gain.setValueAtTime(bassVol, time);
      bassGain.gain.exponentialRampToValueAtTime(0.001, time + stepDuration * 0.8);
    }

    // Melody (only plays at higher tension)
    const melodyNote = battleMelody[step % battleMelody.length];
    if (melodyNote > 0 && melodyOsc && melodyGain && tension > 0.3) {
      melodyOsc.frequency.setValueAtTime(melodyNote, time);
      const melodyVol = 0.18 * (tension - 0.3) * 1.4;
      melodyGain.gain.setValueAtTime(melodyVol, time);
      melodyGain.gain.exponentialRampToValueAtTime(0.01, time + stepDuration * 1.2);
    } else if (melodyGain) {
      melodyGain.gain.setTargetAtTime(0.001, time, 0.02);
    }

    // Power chords
    const chordValue = chordPattern[step % chordPattern.length];
    if (chordValue > 0 && chordOsc1 && chordGain1 && chordOsc2 && chordGain2) {
      chordOsc1.frequency.setValueAtTime(chord.root, time);
      chordOsc2.frequency.setValueAtTime(chord.fifth, time);
      const chordVol = 0.08 * chordValue * (0.4 + tension * 0.6);
      chordGain1.gain.setValueAtTime(chordVol, time);
      chordGain1.gain.exponentialRampToValueAtTime(0.001, time + stepDuration * 0.6);
      chordGain2.gain.setValueAtTime(chordVol * 0.8, time);
      chordGain2.gain.exponentialRampToValueAtTime(0.001, time + stepDuration * 0.6);
    }

    // High tension layer (only at high tension)
    if (tension > 0.6 && tensionOsc && tensionGain) {
      const tensionNote = [NOTE.E5, NOTE.G5, NOTE.A5, NOTE.B5][step % 4];
      tensionOsc.frequency.setValueAtTime(tensionNote, time);
      const tensionVol = 0.05 * (tension - 0.6) * 2.5;
      tensionGain.gain.setValueAtTime(tensionVol, time);
      tensionGain.gain.exponentialRampToValueAtTime(0.001, time + stepDuration * 0.4);
    }

    // Arpeggio (adds movement)
    const arpValue = arpPattern[step % arpPattern.length];
    if (arpValue > 0 && arpOsc && arpGain && tension > 0.4) {
      const arpNotes = [chord.root * 2, chord.fifth * 1.5, chord.root * 2.5, chord.fifth * 2];
      const arpNote = arpNotes[step % 4];
      arpOsc.frequency.setValueAtTime(arpNote, time);
      const arpVol = 0.04 * arpValue * (tension - 0.4) * 1.7;
      arpGain.gain.setValueAtTime(arpVol, time);
      arpGain.gain.exponentialRampToValueAtTime(0.001, time + stepDuration * 0.5);
    }

    // Kick drum
    const kickValue = kickPattern[step % kickPattern.length];
    if (kickValue > 0 && kickOsc && kickGain) {
      kickOsc.frequency.setValueAtTime(80, time);
      kickOsc.frequency.exponentialRampToValueAtTime(40, time + 0.08);
      const kickVol = 0.15 * kickValue * (0.5 + tension * 0.5);
      kickGain.gain.setValueAtTime(kickVol, time);
      kickGain.gain.exponentialRampToValueAtTime(0.001, time + 0.12);
    }

    // Snare hits at high tension (create one-shot oscillator)
    if (tension > 0.5 && step % 8 === 4) {
      const snare = ctx.createOscillator();
      const snareGain = ctx.createGain();
      const snareFilter = ctx.createBiquadFilter();
      snare.type = "triangle";
      snare.frequency.value = 180;
      snareFilter.type = "highpass";
      snareFilter.frequency.value = 800;
      snareGain.gain.setValueAtTime(0.08 * tension, time);
      snareGain.gain.exponentialRampToValueAtTime(0.001, time + 0.08);
      snare.connect(snareFilter);
      snareFilter.connect(snareGain);
      snareGain.connect(this.masterGain!);
      snare.start(time);
      snare.stop(time + 0.1);
    }
  }

  stop() {
    if (!this.playing) return;

    this.playing = false;

    if (this.intervalId) {
      clearTimeout(this.intervalId);
      this.intervalId = null;
    }

    this.oscillators.forEach((osc) => {
      try {
        osc.stop();
        osc.disconnect();
      } catch (e) {}
    });
    this.oscillators = [];

    this.gains.forEach((gain) => {
      try {
        gain.disconnect();
      } catch (e) {}
    });
    this.gains = [];

    if (this.masterGain) {
      try {
        this.masterGain.disconnect();
      } catch (e) {}
      this.masterGain = null;
    }

    if (this.compressor) {
      try {
        this.compressor.disconnect();
      } catch (e) {}
      this.compressor = null;
    }

    console.log("[MusicConductor] Stopped");
  }

  update(snapshot: MusicSnapshot) {
    this.currentTerrain = snapshot.terrain;
    this.currentTension = this.computeTension(snapshot);

    if (snapshot.phase === "waveComplete") {
      this.playStinger("victory");
    } else if (snapshot.hasBoss && snapshot.aliveEnemies <= Math.max(1, Math.ceil(snapshot.totalEnemies * 0.2))) {
      this.playStinger("bossLow");
    }
  }

  private computeTension(snapshot: MusicSnapshot) {
    const density = snapshot.totalEnemies > 0 ? snapshot.aliveEnemies / snapshot.totalEnemies : 0;
    const healthPressure = 1 - clamp01(snapshot.playerHealthPct);
    const boss = snapshot.hasBoss ? 0.2 : 0;
    const coop = snapshot.isMultiplayer ? 0.1 : 0;
    const wavePressure = clamp01(snapshot.wave / 10) * 0.15;

    const raw = 0.3 + density * 0.35 + healthPressure * 0.3 + boss + coop + wavePressure;
    return clamp01(raw);
  }

  private playStinger(type: "victory" | "bossLow") {
    const ctx = this.getAudioContext();
    if (!ctx || !this.masterGain) return;

    const now = ctx.currentTime;

    if (type === "victory") {
      // Triumphant fanfare (C major arpeggio)
      const notes = [NOTE.C5, NOTE.E5, NOTE.G5, NOTE.C5 * 2];
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.value = freq;
        const startTime = now + i * 0.08;
        gain.gain.setValueAtTime(0.25, startTime);
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + 0.3);
        osc.connect(gain);
        gain.connect(this.masterGain!);
        osc.start(startTime);
        osc.stop(startTime + 0.35);
      });
    } else {
      // Boss low warning (minor chord)
      const notes = [NOTE.A4, NOTE.C5, NOTE.E5];
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sawtooth";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.2, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
        osc.connect(gain);
        gain.connect(this.masterGain!);
        osc.start(now);
        osc.stop(now + 0.5);
      });
    }
  }
}

export function getGlobalAudioContext() {
  if (typeof window === "undefined") return null;
  return Howler.ctx ?? null;
}
